import { useUser } from "#hooks/use-auth"
import { useUpdateTourStatus } from "#hooks/use-tour-status"
import type { StepType } from "@reactour/tour"
import { TourProvider, useTour } from "@reactour/tour"
import React from "react"
import { tourStyles } from "../../tours/tour-styles"
import { TourNavigation } from "./TourNavigation"
import { TourProgressTracker } from "./TourProgressTracker"

interface TourProviderWrapperProps {
  steps: StepType[]
  initialStep?: number
  onSkip?: () => void | Promise<void>
  autoStart?: boolean
  needsOnboarding?: boolean
  children: React.ReactNode
}

export function TourProviderWrapper({
  steps,
  initialStep = 0,
  onSkip,
  autoStart = false,
  needsOnboarding = false,
  children,
}: TourProviderWrapperProps) {
  const { data: user } = useUser()
  const updateTourStatus = useUpdateTourStatus()

  const isTourCompleted = user?.tourCompletedAt !== null && user?.tourCompletedAt !== undefined
  const lastTourStep = user?.tourStepIndex ?? 0
  const prevIsCompletedRef = React.useRef<boolean>(isTourCompleted)
  const initialStepRef = React.useRef<number | null>(null)

  if (prevIsCompletedRef.current && !isTourCompleted) {
    initialStepRef.current = null
  }
  prevIsCompletedRef.current = isTourCompleted

  const actualInitialStep = React.useMemo(() => {
    if (initialStepRef.current !== null) {
      return initialStepRef.current
    }
    let step = initialStep
    if (needsOnboarding) {
      step = 0
    } else if (!isTourCompleted && lastTourStep > 0) {
      step = lastTourStep
    }
    initialStepRef.current = step
    return step
  }, [needsOnboarding, isTourCompleted, initialStep])

  const skipRef = React.useRef<boolean>(false)

  const handleSkipTour = React.useCallback(async () => {
    skipRef.current = true

    const now = new Date().toISOString()
    try {
      const tourStartedAt = user?.tourStartedAt ? new Date(user.tourStartedAt).toISOString() : now

      await updateTourStatus.mutateAsync({
        tourStartedAt,
        tourCompletedAt: now,
      })
      if (onSkip) {
        await onSkip()
      }
    } catch (error) {
      console.error("Failed to skip tour:", error)
    }
  }, [updateTourStatus, user?.tourStartedAt, onSkip])

  const tourKey = React.useMemo(
    () => `tour-${needsOnboarding}-${isTourCompleted}`,
    [needsOnboarding, isTourCompleted]
  )

  return (
    <TourProvider
      key={tourKey}
      steps={steps}
      styles={tourStyles}
      afterOpen={(target) => {
        if (target) {
          // Use requestAnimationFrame to ensure layout is stable before scrolling
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              target.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
              })
            })
          })
        }
      }}
      padding={{ mask: 10, popover: [10, 10] }}
      className="reactour__popover"
      components={{
        Navigation: () => <TourNavigation onSkip={handleSkipTour} />,
      }}
      disableInteraction={false}
    >
      <TourInitialStepSetter initialStep={actualInitialStep} />
      <TourProgressTracker
        isTourCompleted={isTourCompleted}
        user={user}
        updateTourStatus={updateTourStatus}
        totalSteps={steps.length}
        needsOnboarding={needsOnboarding}
        skipRef={skipRef}
      />
      {autoStart && !isTourCompleted && (
        <AutoStartTour isTourCompleted={isTourCompleted} needsOnboarding={needsOnboarding} />
      )}
      {children}
    </TourProvider>
  )
}

function TourInitialStepSetter({ initialStep }: { initialStep: number }) {
  const { setCurrentStep, isOpen } = useTour()
  const hasSetInitialStep = React.useRef(false)
  const prevInitialStep = React.useRef<number>(initialStep)

  React.useEffect(() => {
    if (prevInitialStep.current !== initialStep) {
      hasSetInitialStep.current = false
      prevInitialStep.current = initialStep
    }
  }, [initialStep])

  React.useEffect(() => {
    if (isOpen && !hasSetInitialStep.current) {
      setCurrentStep(initialStep)
      hasSetInitialStep.current = true
    }
  }, [isOpen, initialStep, setCurrentStep])

  return null
}

function AutoStartTour({
  isTourCompleted,
  needsOnboarding,
}: {
  isTourCompleted: boolean
  needsOnboarding: boolean
}) {
  const { setIsOpen, isOpen } = useTour()
  const hasStartedRef = React.useRef(false)
  const prevIsCompletedRef = React.useRef<boolean>(isTourCompleted)

  React.useEffect(() => {
    if (prevIsCompletedRef.current && !isTourCompleted) {
      hasStartedRef.current = false
    }
    prevIsCompletedRef.current = isTourCompleted
  }, [isTourCompleted])

  React.useEffect(() => {
    if (hasStartedRef.current && isOpen) return

    if (isTourCompleted) {
      return
    }

    const shouldStartTour = needsOnboarding || !isTourCompleted

    if (shouldStartTour && !isOpen) {
      // Wait for layout to be ready before opening tour
      // Use requestAnimationFrame to ensure DOM is ready
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsOpen(true)
            hasStartedRef.current = true
          })
        })
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [needsOnboarding, isTourCompleted, setIsOpen, isOpen])

  React.useEffect(() => {
    if (!isOpen) {
      hasStartedRef.current = false
    }
  }, [isOpen])

  return null
}
